import React, { useState, useEffect } from 'react';
import { axiosInstance as axios } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { DollarSign, Plus, PiggyBank, ArrowRightLeft, Users, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const API = '/api';

export function ContributionsTab({ familyId, family, members, proxyMembers, currentUser, isOwner }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showAdminForm, setShowAdminForm] = useState(false);
  const [showAllocateForm, setShowAllocateForm] = useState(false);
  const [events, setEvents] = useState([]);
  
  // Form states
  const [amount, setAmount] = useState('');
  const [purpose, setPurpose] = useState('');
  const [notes, setNotes] = useState('');
  const [adminMemberId, setAdminMemberId] = useState('');
  const [adminMemberName, setAdminMemberName] = useState('');
  const [adminIsProxy, setAdminIsProxy] = useState(false);
  const [allocAmount, setAllocAmount] = useState('');
  const [allocPurpose, setAllocPurpose] = useState('');
  const [allocEventId, setAllocEventId] = useState('');
  const [allocEventName, setAllocEventName] = useState('');

  useEffect(() => {
    fetchData();
    fetchEvents();
  }, [familyId]);

  const fetchData = async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/contributions`, { withCredentials: true });
      setData(res.data);
    } catch (err) {
      console.error('Failed to load contributions:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchEvents = async () => {
    try {
      const res = await axios.get(`${API}/families/${familyId}/events`, { withCredentials: true });
      setEvents(res.data.events || res.data || []);
    } catch (err) {
      console.error('Failed to load events:', err);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/families/${familyId}/contributions`, {
        amount: parseFloat(amount),
        purpose: purpose || 'General Family Fund',
        notes
      }, { withCredentials: true });
      toast.success('Contribution recorded!');
      setShowAddForm(false);
      setAmount(''); setPurpose(''); setNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record contribution');
    }
  };

  const handleAdminAdd = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/families/${familyId}/contributions/admin`, {
        member_id: adminMemberId,
        member_name: adminMemberName,
        amount: parseFloat(amount),
        purpose: purpose || 'General Family Fund',
        notes,
        is_proxy: adminIsProxy
      }, { withCredentials: true });
      toast.success('Contribution recorded!');
      setShowAdminForm(false);
      setAmount(''); setPurpose(''); setNotes(''); setAdminMemberId(''); setAdminMemberName('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record contribution');
    }
  };

  const handleAllocate = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/families/${familyId}/contributions/allocate`, {
        amount: parseFloat(allocAmount),
        purpose: allocPurpose,
        event_id: allocEventId || null,
        event_name: allocEventName || null,
        notes
      }, { withCredentials: true });
      toast.success('Funds allocated!');
      setShowAllocateForm(false);
      setAllocAmount(''); setAllocPurpose(''); setAllocEventId(''); setAllocEventName(''); setNotes('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to allocate funds');
    }
  };

  const allMembers = [
    ...(members || []).map(m => ({ ...m, is_proxy: false })),
    ...(proxyMembers || []).map(p => ({ user_id: p.proxy_id, name: p.name, is_proxy: true }))
  ];

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading contributions...</div>;
  }

  const summary = data?.summary || { total_contributed: 0, total_allocated: 0, available_balance: 0, contributor_count: 0 };

  return (
    <div className="space-y-6" data-testid="contributions-tab">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-4 text-center">
            <PiggyBank className="h-6 w-6 text-green-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-green-700" data-testid="total-contributed">${summary.total_contributed.toFixed(2)}</p>
            <p className="text-sm text-green-600">Total Contributed</p>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50">
          <CardContent className="p-4 text-center">
            <ArrowRightLeft className="h-6 w-6 text-blue-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-blue-700" data-testid="total-allocated">${summary.total_allocated.toFixed(2)}</p>
            <p className="text-sm text-blue-600">Allocated to Events</p>
          </CardContent>
        </Card>
        <Card className="border-amber-200 bg-amber-50">
          <CardContent className="p-4 text-center">
            <DollarSign className="h-6 w-6 text-amber-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-amber-700" data-testid="available-balance">${summary.available_balance.toFixed(2)}</p>
            <p className="text-sm text-amber-600">Available Balance</p>
          </CardContent>
        </Card>
        <Card className="border-purple-200 bg-purple-50">
          <CardContent className="p-4 text-center">
            <Users className="h-6 w-6 text-purple-600 mx-auto mb-1" />
            <p className="text-2xl font-bold text-purple-700" data-testid="contributor-count">{summary.contributor_count}</p>
            <p className="text-sm text-purple-600">Contributors</p>
          </CardContent>
        </Card>
      </div>

      {/* Action Buttons */}
      <div className="flex flex-wrap gap-3">
        <button
          onClick={() => { setShowAddForm(true); setShowAdminForm(false); setShowAllocateForm(false); }}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition"
          data-testid="add-contribution-btn"
        >
          <Plus className="h-4 w-4" />
          Add My Contribution
        </button>
        {isOwner && (
          <>
            <button
              onClick={() => { setShowAdminForm(true); setShowAddForm(false); setShowAllocateForm(false); }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition"
              data-testid="record-for-member-btn"
            >
              <Plus className="h-4 w-4" />
              Record for Member
            </button>
            <button
              onClick={() => { setShowAllocateForm(true); setShowAddForm(false); setShowAdminForm(false); }}
              className="flex items-center gap-2 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium transition"
              data-testid="allocate-funds-btn"
            >
              <ArrowRightLeft className="h-4 w-4" />
              Allocate Funds
            </button>
          </>
        )}
      </div>

      {/* Add My Contribution Form */}
      {showAddForm && (
        <Card className="border-green-200">
          <CardHeader><CardTitle className="text-green-700 text-lg">Add Your Contribution</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleAdd} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                  <input type="number" step="0.01" min="0.01" value={amount} onChange={e => setAmount(e.target.value)} required
                    className="w-full border rounded-lg px-3 py-2" data-testid="contribution-amount" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                  <input type="text" value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="General Family Fund"
                    className="w-full border rounded-lg px-3 py-2" data-testid="contribution-purpose" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notes (optional)</label>
                <input type="text" value={notes} onChange={e => setNotes(e.target.value)} placeholder="Any notes..."
                  className="w-full border rounded-lg px-3 py-2" />
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium" data-testid="submit-contribution">Save</button>
                <button type="button" onClick={() => setShowAddForm(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Admin Record Form */}
      {showAdminForm && isOwner && (
        <Card className="border-blue-200">
          <CardHeader><CardTitle className="text-blue-700 text-lg">Record Contribution for Member</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={handleAdminAdd} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Member</label>
                <select value={adminMemberId} onChange={e => {
                  const m = allMembers.find(x => x.user_id === e.target.value);
                  setAdminMemberId(e.target.value);
                  setAdminMemberName(m?.name || '');
                  setAdminIsProxy(m?.is_proxy || false);
                }} required className="w-full border rounded-lg px-3 py-2" data-testid="admin-member-select">
                  <option value="">Select a member...</option>
                  {allMembers.map(m => (
                    <option key={m.user_id} value={m.user_id}>{m.name}{m.is_proxy ? ' (proxy)' : ''}</option>
                  ))}
                </select>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                  <input type="number" step="0.01" min="0.01" value={amount} onChange={e => setAmount(e.target.value)} required
                    className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                  <input type="text" value={purpose} onChange={e => setPurpose(e.target.value)} placeholder="General Family Fund"
                    className="w-full border rounded-lg px-3 py-2" />
                </div>
              </div>
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">Save</button>
                <button type="button" onClick={() => setShowAdminForm(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Allocate Form */}
      {showAllocateForm && isOwner && (
        <Card className="border-amber-200">
          <CardHeader><CardTitle className="text-amber-700 text-lg">Allocate Funds</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-gray-500 mb-4">Available: <strong className="text-amber-700">${summary.available_balance.toFixed(2)}</strong></p>
            <form onSubmit={handleAllocate} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Amount ($)</label>
                  <input type="number" step="0.01" min="0.01" max={summary.available_balance} value={allocAmount}
                    onChange={e => setAllocAmount(e.target.value)} required className="w-full border rounded-lg px-3 py-2" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Purpose</label>
                  <input type="text" value={allocPurpose} onChange={e => setAllocPurpose(e.target.value)} required
                    placeholder="e.g., Family Reunion 2026" className="w-full border rounded-lg px-3 py-2" />
                </div>
              </div>
              {events.length > 0 && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Link to Event (optional)</label>
                  <select value={allocEventId} onChange={e => {
                    const ev = events.find(x => x.event_id === e.target.value);
                    setAllocEventId(e.target.value);
                    setAllocEventName(ev?.name || '');
                  }} className="w-full border rounded-lg px-3 py-2">
                    <option value="">No specific event</option>
                    {events.map(ev => (
                      <option key={ev.event_id} value={ev.event_id}>{ev.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="flex gap-2">
                <button type="submit" className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 font-medium">Allocate</button>
                <button type="button" onClick={() => setShowAllocateForm(false)} className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300">Cancel</button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Contributions History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-green-600" />
            Contribution History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data?.contributions?.length > 0 ? (
            <div className="space-y-3">
              {data.contributions.map(c => (
                <div key={c.contribution_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border" data-testid="contribution-row">
                  <div>
                    <p className="font-medium text-gray-900">{c.contributor_name}</p>
                    <p className="text-sm text-gray-500">{c.purpose}{c.notes ? ` — ${c.notes}` : ''}</p>
                    <p className="text-xs text-gray-400">{new Date(c.created_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-lg font-bold text-green-700">${c.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-400">
              <PiggyBank className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>No contributions yet. Be the first to contribute to the family fund!</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Allocations History */}
      {data?.allocations?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <ArrowRightLeft className="h-5 w-5 text-amber-600" />
              Fund Allocations
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {data.allocations.map(a => (
                <div key={a.allocation_id} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg border border-amber-200">
                  <div>
                    <p className="font-medium text-gray-900">{a.purpose}</p>
                    {a.event_name && <Badge className="bg-blue-100 text-blue-700 mt-1">{a.event_name}</Badge>}
                    <p className="text-xs text-gray-400 mt-1">by {a.allocated_by_name} on {new Date(a.allocated_at).toLocaleDateString()}</p>
                  </div>
                  <span className="text-lg font-bold text-amber-700">${a.amount.toFixed(2)}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
